/**
 * PATH: apps/quote/src/hooks/usePasteMappingPresets.ts
 * PURPOSE: Per-org persistence for paste column mapping presets
 *
 * WHY:
 * - Users paste from the same Excel templates repeatedly
 * - Saving their column mappings saves significant time
 * - Different orgs may have different Excel formats
 *
 * STORAGE KEY FORMAT:
 *   `quotech:paste-mappings:${orgId}:${presetName}`
 */

import { useCallback, useMemo } from 'react'
import { useAuth } from '@quotech/auth'

export interface ColumnMapping {
  sourceIndex: number
  targetField: string
}

export interface PasteMappingPreset {
  id: string
  name: string
  mappings: ColumnMapping[]
  createdAt: string
  updatedAt: string
  useCount: number
}

const STORAGE_PREFIX = 'quotech:paste-mappings'
const MAX_PRESETS = 10

function getStorageKey(orgId: string | number): string {
  return `${STORAGE_PREFIX}:${orgId}`
}

export function usePasteMappingPresets() {
  const { user } = useAuth()
  const orgId = user?.organisation_id ?? 'default'

  // Get all presets for this org
  const presets = useMemo(() => {
    try {
      const key = getStorageKey(orgId)
      const stored = localStorage.getItem(key)
      if (stored) {
        const parsed = JSON.parse(stored) as PasteMappingPreset[]
        // Sort by use count (most used first)
        return parsed.sort((a, b) => b.useCount - a.useCount)
      }
    } catch (e) {
      console.warn('Failed to load paste mapping presets:', e)
    }
    return []
  }, [orgId])

  // Save a new preset or update existing
  const savePreset = useCallback((name: string, mappings: ColumnMapping[]) => {
    try {
      const key = getStorageKey(orgId)
      const stored = localStorage.getItem(key)
      let allPresets: PasteMappingPreset[] = stored ? JSON.parse(stored) : []
      
      // Check if preset with this name exists
      const existingIndex = allPresets.findIndex(p => p.name === name)
      const now = new Date().toISOString()
      
      if (existingIndex >= 0) {
        // Update existing
        allPresets[existingIndex] = {
          ...allPresets[existingIndex],
          mappings,
          updatedAt: now,
          useCount: allPresets[existingIndex].useCount + 1,
        }
      } else {
        // Create new
        const newPreset: PasteMappingPreset = {
          id: `preset-${Date.now()}`,
          name,
          mappings,
          createdAt: now,
          updatedAt: now,
          useCount: 0,
        }
        allPresets = [newPreset, ...allPresets]
      }
      
      // Limit to MAX_PRESETS (keep most used)
      if (allPresets.length > MAX_PRESETS) {
        allPresets = allPresets
          .sort((a, b) => b.useCount - a.useCount)
          .slice(0, MAX_PRESETS)
      }
      
      localStorage.setItem(key, JSON.stringify(allPresets))
      return true
    } catch (e) {
      console.error('Failed to save paste mapping preset:', e)
      return false
    }
  }, [orgId])

  // Delete a preset
  const deletePreset = useCallback((presetId: string) => {
    try {
      const key = getStorageKey(orgId)
      const stored = localStorage.getItem(key)
      if (!stored) return false
      
      let allPresets: PasteMappingPreset[] = JSON.parse(stored)
      allPresets = allPresets.filter(p => p.id !== presetId)
      localStorage.setItem(key, JSON.stringify(allPresets))
      return true
    } catch (e) {
      console.error('Failed to delete paste mapping preset:', e)
      return false
    }
  }, [orgId])

  // Increment use count for a preset
  const incrementUseCount = useCallback((presetId: string) => {
    try {
      const key = getStorageKey(orgId)
      const stored = localStorage.getItem(key)
      if (!stored) return
      
      const allPresets: PasteMappingPreset[] = JSON.parse(stored)
      const preset = allPresets.find(p => p.id === presetId)
      if (preset) {
        preset.useCount += 1
        preset.updatedAt = new Date().toISOString()
        localStorage.setItem(key, JSON.stringify(allPresets))
      }
    } catch (e) {
      console.warn('Failed to increment preset use count:', e)
    }
  }, [orgId])

  // Get the default/most-used preset
  const defaultPreset = presets[0] ?? null

  // Find preset by matching mappings
  const findMatchingPreset = useCallback((headerRow: string[]): PasteMappingPreset | null => {
    // Simple heuristic: check if header row contains similar column names
    const normalizedHeaders = headerRow.map(h => h.toLowerCase().trim())
    
    for (const preset of presets) {
      // Check if the number of mappings roughly matches
      if (preset.mappings.length === 0) continue
      
      // Count how many mappings might apply
      let matchCount = 0
      for (const mapping of preset.mappings) {
        if (mapping.sourceIndex < normalizedHeaders.length) {
          matchCount++
        }
      }
      
      // If most mappings apply, consider it a match
      if (matchCount >= preset.mappings.length * 0.7) {
        return preset
      }
    }
    
    return null
  }, [presets])

  return {
    presets,
    defaultPreset,
    savePreset,
    deletePreset,
    incrementUseCount,
    findMatchingPreset,
  }
}

export default usePasteMappingPresets

